import { useContext, useEffect, useState, type ChangeEvent, type MouseEvent } from 'react';
import { Tooltip } from 'bootstrap';
import { SmartContext } from '../../../library/Core/SmartContext';
import { DomainElement } from '../../../library/Core/SmartTypes';
import { dedupeFreeTextFilterOptions, resolveDirectoryFilterItemClass } from '../../../library/Core/SmartFunctions';
import DomainExpertDirectoryService from './DomainExpertDirectoryService';
import AlumniDirectoryService from '../alumni-directory/AlumniDirectoryService';
import FactLoader from '../../../library/SimpleControls/FactLoader';
import DirectoryFilterDomainCheckboxes, { DirectoryShowProfileSection } from '../shared/DirectoryFilterDomainCheckboxes';
import { getActiveShowProfileFiltersFromConfig } from '../shared/directoryAppliedFilters';
import { STAGES } from '../../watchlists/report/labelMaps';

// ── exported so SummaryCardControl can call it ─────────────────────────────
export const handleClearAllFilterClick = (
    event: MouseEvent<HTMLElement>,
    state: any,
    dispatch: any
) => {
    event.preventDefault();
    const defaultCriteria = DomainExpertDirectoryService.getDefaultSearchCriteria();
    const criteriaForSelectedCard = DomainExpertDirectoryService.getCriteriaForSelectedCard('totalDomainExperts', state);

     let criteria = { ...defaultCriteria, ...criteriaForSelectedCard };
    criteria = { ...criteria,'context': { ...criteria.context, selectedCard : 'totalDomainExperts' } };

    dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'filters', value: criteria } });
    // Signal DomainExpertSearchControl to clear per-column filter search text
    dispatch({
        type: 'CONTROL_VALUE_CHANGE',
        payload: { dataKey: 'filterSearchTermsResetToken', value: Date.now() },
    });
    DomainExpertDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch);
};

interface ExpandedControls { [key: string]: boolean; }
type YearDomain = { code: string | number; value: string };

// ── Decade-bucket helper (same logic as RecruiterSearchControl) ────────────
const makeDecadeBuckets = (domains: YearDomain[], foundingYear: number = 1950) => {
    if (!domains?.length) return { beforeLabel: '', beforeYears: [] as YearDomain[], groups: [] as any[] };

    const currentYear = new Date().getFullYear();
    const nums = domains.map((d) => parseInt(String(d.code), 10)).filter(Number.isFinite).sort((a, b) => a - b);
    if (!nums.length) return { beforeLabel: '', beforeYears: [], groups: [] };

    const minYear = nums[0];
    const maxYear = nums[nums.length - 1];

    // Start decade from the decade that covers the founding year
    const startDecade = Math.floor(Math.max(foundingYear, minYear) / 10) * 10;
    const endDecade   = Math.floor(maxYear / 10) * 10;

    const beforeYears = domains
        .filter((d) => parseInt(String(d.code), 10) < startDecade)
        .sort((a, b) => parseInt(String(a.code), 10) - parseInt(String(b.code), 10));

    const groups: any[] = [];
    for (let d = startDecade; d <= endDecade; d += 10) {
        const to = d === endDecade ? currentYear : d + 9;
        const label = d === endDecade ? `${d}-${currentYear}` : `${d}-${d + 9}`;
        const years = domains
            .filter((x) => {
                const y = parseInt(String(x.code), 10);
                return y >= d && y <= (d === endDecade ? currentYear : d + 9);
            })
            .sort((a, b) => parseInt(String(a.code), 10) - parseInt(String(b.code), 10));
        if (years.length) groups.push({ from: d, to, label, years });
    }

    return {
        beforeLabel: beforeYears.length ? `Before ${startDecade}` : '',
        beforeYears,
        groups,
    };
};

// ── Component ─────────────────────────────────────────────────────────────
const DomainExpertSearchControl = () => {
    const uniqueCodes = new Set<string>();
    const { state, dispatch } = useContext(SmartContext);

    const [performSearch, setPerformSearch]           = useState(0);
    const [loading, setLoading]                       = useState(false);
    const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
    const [searchTerms, setSearchTerms]               = useState({} as any);
    const filterSearchTermsResetToken = state?.data?.filterSearchTermsResetToken;
    useEffect(() => {
        if (filterSearchTermsResetToken) {
            setSearchTerms({});
        }
    }, [filterSearchTermsResetToken]);
    const [showFilters, setShowFilters]               = useState(true);
    const [expandedControls, setExpandedControls]     = useState<ExpandedControls>({});
    const [expandedDecades, setExpandedDecades]       = useState<Record<string, boolean>>({});
    const [isOpen, setOpen]                           = useState(false);

    const searchFilterDomains = (state as any).data?.searchDomain || {};
    const filters = Object.values(DomainExpertDirectoryService.filterConfig);
    const hasBurnerAnalysis = !!(state as any).data?.hasBurnerAnalysis;

    useEffect(() => {
        const els = [].slice.call(document.querySelectorAll('.defaultTooltip'));
        const tips = els.map((el: any) => new Tooltip(el));
        return () => tips.forEach((t: any) => t.dispose());
    });

    useEffect(() => {
        let active = true;

        // pageLoader already fired the default search in parallel with other page-config
        // calls and stored results in state.data.students — skip the duplicate /search
        // call so the user sees results immediately without a second round-trip.
        if (performSearch === 0 && (state as any).data?.preloadedSearch) {
            setLoading(false);
            return () => { active = false; };
        }

        setLoading(true);
        const criteria =
            performSearch === 0
                ? {
                      ...DomainExpertDirectoryService.getCriteriaForSelectedCard('totalDomainExperts', state),
                      ...(state as any).data?.filters,
                  }
                : (state as any).data?.filters;

        DomainExpertDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch).finally(
            () => { if (active) setLoading(false); }
        );
        return () => { active = false; };
    }, [performSearch]);

    const updateFilters = (type: string, dataKey: string, value: any) => {
        dispatch({ type, payload: { dataKey, value, errorMessages: [] } });
        setPerformSearch((prev) => prev + 1);
    };

    // Clear any child filters whose parent is the given filter id
    const clearChildFilters = (parentId: string) => {
        const childFilters = Object.values(DomainExpertDirectoryService.filterConfig)
            .filter((cfg: any) => cfg.parentId === parentId);
        childFilters.forEach((child: any) => {
            const currentValues = (state as any).data?.filters?.[child.id] || [];
            if (currentValues.length > 0) {
                dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: `filters.${child.id}`, value: [], errorMessages: [] } });
            }
        });
    };

    const handleSearchListItemSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const { id, value, checked } = event.target;

        if (id === 'domainExpertiseArea' && !checked) {
            const current: string[] = (state as any).data?.filters?.domainExpertiseArea || [];
            const removeKey = String(value).toLowerCase();
            current
                .filter((v) => String(v).toLowerCase() === removeKey)
                .forEach((v) => updateFilters('DELETE_RECORD_IN_SIMPLE_ARRAY', `filters.${id}`, v));
            return;
        }

        const type = checked ? 'CHECKBOX_ADD_NEW_RECORD_IN_ARRAY' : 'DELETE_RECORD_IN_SIMPLE_ARRAY';
        clearChildFilters(id);

        if (id === 'domainExpertiseArea' && checked) {
            const current: string[] = (state as any).data?.filters?.domainExpertiseArea || [];
            const exists = current.some((v) => String(v).toLowerCase() === String(value).toLowerCase());
            if (exists) return;
        }

        updateFilters(type, `filters.${id}`, value);
    };

    const handleBooleanFilterChange = (filterKey: string) => (event: ChangeEvent<HTMLInputElement>) => {
        const checkedValue =
            DomainExpertDirectoryService.filterConfig[filterKey]?.booleanCheckedValue ?? true;
        updateFilters('CONTROL_VALUE_CHANGE', `filters.${filterKey}`, event.target.checked ? checkedValue : null);
    };

    const handleSearchChange = (controlId: string) => (event: ChangeEvent<HTMLInputElement>) => {
        setSearchTerms((prev: any) => ({ ...prev, [controlId]: event.target.value }));
    };

    const handleAppliedFilterClick = (event: any, key: string, value: string) => {
        event.preventDefault();
        clearChildFilters(key);

        if (key === 'domainExpertiseArea') {
            const current: string[] = (state as any).data?.filters?.domainExpertiseArea || [];
            const removeKey = String(value).toLowerCase();
            current
                .filter((v) => String(v).toLowerCase() === removeKey)
                .forEach((v) => updateFilters('DELETE_RECORD_IN_SIMPLE_ARRAY', `filters.${key}`, v));
            return;
        }

        updateFilters('DELETE_RECORD_IN_SIMPLE_ARRAY', `filters.${key}`, value);
    };

    const handleBooleanFilterClear = (event: MouseEvent, key: string) => {
        event.preventDefault();
        updateFilters('CONTROL_VALUE_CHANGE', `filters.${key}`, null);
    };

    const handleFilterToggle = () => {
        setOpen(!isOpen);
        setShowAdditionalFilters(!showAdditionalFilters);
    };

    const toggleControl = (controlId: string) => {
        setExpandedControls((prev) => ({ [controlId]: !prev[controlId] }));
    };

    const toggleDecade = (label: string) => {
        setExpandedDecades((prev) => ({ ...prev, [label]: !prev[label] }));
    };

    // Get domain items for a control, filtered by parent if parentIdApply is set
    const getFilteredDomainItems = (control: any) => {
        let items = searchFilterDomains[control.id] || [];

        if (control.parentIdApply && control.parentId) {
            const parentValues = (state as any).data?.filters?.[control.parentId] || [];
            if (!parentValues.length) {
                return { items: [], needsParent: true, parentLabel: DomainExpertDirectoryService.filterConfig[control.parentId]?.label || control.parentId };
            }
            // Filter by parent code if domain items have parentCode
            items = items.filter((d: any) => {
                if (d.parentCode) return parentValues.map(String).includes(String(d.parentCode));
                return true;
            });
        }

        return { items, needsParent: false, parentLabel: '' };
    };

    const getSelectedPassOutYears = () => ((state as any).data?.filters?.passOutYear || []).map(String);

    const isYearGroupFullySelected = (years: YearDomain[]) => {
        const selected = getSelectedPassOutYears();
        return years.length > 0 && years.every((y) => selected.includes(String(y.code)));
    };

    const isYearGroupPartiallySelected = (years: YearDomain[]) => {
        const selected = getSelectedPassOutYears();
        const selectedCount = years.filter((y) => selected.includes(String(y.code))).length;
        return selectedCount > 0 && selectedCount < years.length;
    };

    const handleYearGroupSelectAll = (label: string, years: YearDomain[]) => (event: ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const yearCodes = years.map((y) => String(y.code));
        const selected = getSelectedPassOutYears();
        const allSelected = yearCodes.every((c) => selected.includes(c));
        const next = allSelected
            ? selected.filter((c: string) => !yearCodes.includes(c))
            : [...new Set([...selected, ...yearCodes])];
        setExpandedDecades((prev) => ({ ...prev, [label]: !allSelected }));
        updateFilters('CONTROL_VALUE_CHANGE', 'filters.passOutYear', next);
    };

    const yearCheckbox = (yd: YearDomain) => {
        const codeStr = String(yd.code);
        const checked = getSelectedPassOutYears().includes(codeStr);
        return (
            <div key={codeStr} className="form-check d-flex flex-wrap ms-1">
                <input
                    id="passOutYear"
                    className="form-check-input me-2"
                    type="checkbox"
                    value={yd.code}
                    name="passOutYear_Name"
                    checked={checked}
                    onChange={handleSearchListItemSelect}
                    onClick={(e) => e.stopPropagation()}
                />
                <label className="form-check-label font-16 font-400 flex-1 pt-1" onClick={(e) => e.stopPropagation()}>
                    {yd.value}
                </label>
            </div>
        );
    };

    const renderYearGroupRow = (label: string, years: YearDomain[]) => {
        const open = !!expandedDecades[label];
        const allSelected = isYearGroupFullySelected(years);
        const partiallySelected = isYearGroupPartiallySelected(years);
        return (
            <div key={label} className="mb-2">
                <div
                    className={`form-check d-flex flex-wrap align-items-center parent ${open ? '' : 'collapsed'}`}
                    role="button"
                    aria-expanded={open}
                    onClick={() => toggleDecade(label)}>
                    <input
                        className="form-check-input me-2"
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                            if (el) el.indeterminate = partiallySelected;
                        }}
                        onChange={handleYearGroupSelectAll(label, years)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select all ${label}`}
                    />
                    <label className="form-check-label font-16 font-400 flex-1 pt-1">{label}</label>
                </div>
                {open && <div className="child ps-4 show">{years.map(yearCheckbox)}</div>}
            </div>
        );
    };

    const renderFilterItems = (controlList: any[]) => {
        const visibleControls = controlList.filter(
            (control) => !(control.isBurner && !hasBurnerAnalysis)
        );
        const visibleCount = visibleControls.length;

        return visibleControls.map((control: any, index: number) => {
            const isExpanded = !isOpen ? true : index === 0 || !!expandedControls[control.id];

            return (
                <div key={control.id} className={resolveDirectoryFilterItemClass(control.class, visibleCount)}>
                    <div className="filterHeader">
                        <div className="d-flex justify-content-between align-items-center position-relative">
                            <button
                                className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
                                type="button"
                                onClick={() => toggleControl(control.id)}
                                data-bs-toggle="collapse"
                                data-bs-target={`#collapse${control.id}`}
                                aria-expanded={isExpanded ? 'true' : 'false'}>
                                <span className="font-18 font-600">{control.label}</span>
                                <i
                                    className="bi bi-info-circle-fill defaultTooltip user-select-all"
                                    data-bs-toggle="tooltip"
                                    title={control.tooltip}
                                />
                            </button>
                        </div>
                    </div>

                    <div
                        id={`collapse${control.id}`}
                        className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}
                        data-bs-parent="#accordionFilters">

                        {control.hasSearch && (
                            <div className="filterSearchblk">
                                <form className="d-flex align-items-center filterSearch position-relative">
                                    <input
                                        className="form-control flex-1 font-14 font-500 w-100"
                                        type="search"
                                        placeholder="Search"
                                        aria-label="Search"
                                        value={searchTerms[control.id] || ''}
                                        onChange={handleSearchChange(control.id)}
                                    />
                                    <button className="btn icon-search" type="button" />
                                </form>
                            </div>
                        )}

                        <div className="card card-body">
                            <div className="overflow-hidden">
                                <div className="scrollMe">
                                    {/* Batch Year — decade-grouped */}
                                    {control.id === 'passOutYear' ? (
                                        (() => {
                                            const src: YearDomain[] = searchFilterDomains['passOutYear'] ?? [];
                                            const q = (searchTerms['passOutYear'] || '').toLowerCase();
                                            const filtered = !q
                                                ? src
                                                : src.filter((d) =>
                                                      String(d.value).toLowerCase().includes(q) ||
                                                      String(d.code).toLowerCase().includes(q)
                                                  );
                                            const { beforeLabel, beforeYears, groups } = makeDecadeBuckets(filtered);
                                            return (
                                                <>
                                                    {beforeLabel && beforeYears.length > 0 && renderYearGroupRow(beforeLabel, beforeYears)}
                                                    {groups.map((g) => renderYearGroupRow(g.label, g.years))}
                                                </>
                                            );
                                        })()
                                    ) : control.isBurner ? (
                                        /* Burner Analysis — static HIGH/MODERATE/LOW */
                                        searchFilterDomains[control.id]?.map((d: DomainElement) => {
                                            const isChecked = ((state as any).data?.filters?.[control.id] || [])
                                                .map(String)
                                                .includes(String(d.code));
                                            return (
                                                <div key={d.code} className="form-check">
                                                    <input
                                                        id={control.id}
                                                        className="form-check-input me-2"
                                                        type="checkbox"
                                                        value={d.code}
                                                        name={control.id}
                                                        checked={isChecked}
                                                        onChange={handleSearchListItemSelect}
                                                    />
                                                    <label className="form-check-label font-16 font-400 flex-1 ps-1 pt-1">
                                                        {d.value}
                                                    </label>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        /* Standard multi-select checkbox */
                                        (() => {
                                            const { items: domainItems, needsParent, parentLabel } = getFilteredDomainItems(control);
                                            if (needsParent) {
                                                return (
                                                    <div className="text-muted font-14 p-2">
                                                        Select <b>{parentLabel}</b> first
                                                    </div>
                                                );
                                            }
                                            return (
                                                <DirectoryFilterDomainCheckboxes
                                                    controlId={control.id}
                                                    domainCategory={control.domain}
                                                    controlConfig={control}
                                                    items={domainItems}
                                                    searchTerm={searchTerms[control.id] || ''}
                                                    dedupeCodes={control.id === 'programId' ? uniqueCodes : undefined}
                                                    isChecked={(code) => {
                                                        const selected = ((state as any).data?.filters?.[control.id] || []).map(String);
                                                        if (control.id === 'domainExpertiseArea') {
                                                            const codeLower = String(code).toLowerCase();
                                                            return selected.some((s: string) => s.toLowerCase() === codeLower);
                                                        }
                                                        return selected.includes(String(code));
                                                    }}
                                                    onChange={handleSearchListItemSelect}
                                                />
                                            );
                                        })()
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    };

    // Applied filter chips — array filters + Show Experts boolean checkboxes
    let hasAppliedFilters = false;
    const appliedFilterChips = Object.keys((state as any).data?.filters || {}).flatMap((key) => {
        const val = (state as any).data?.filters[key];
        if (!Array.isArray(val)) return [];
        const values =
            key === 'activationStatus'
                ? [...val].sort((a, b) => STAGES.indexOf(String(a)) - STAGES.indexOf(String(b)))
                : key === 'domainExpertiseArea'
                ? dedupeFreeTextFilterOptions(val).map((d) => d.value)
                : val;
        return values.map((v: string) => {
            const cfg = DomainExpertDirectoryService.filterConfig[key];
            if (!cfg) return null;
            const domainItem =
                key === 'domainExpertiseArea'
                    ? searchFilterDomains[key]?.find(
                          (d: DomainElement) => String(d.code).toLowerCase() === String(v).toLowerCase()
                      )
                    : searchFilterDomains[key]?.find((d: DomainElement) => String(d.code) === String(v));
            const label = domainItem?.value ?? v;
            hasAppliedFilters = true;
            return (
                <li key={`${key}_${v}`}>
                    <a href="javascript:;" onClick={(e) => handleAppliedFilterClick(e, key, v)}>
                        {cfg.label}: <b>{label}</b> <i className="icon-cross"></i>
                    </a>
                </li>
            );
        });
    });
    const showProfileFilterChips = getActiveShowProfileFiltersFromConfig(
        (state as any).data?.filters,
        DomainExpertDirectoryService.filterConfig
    ).map(({ key, label }) => {
        hasAppliedFilters = true;
        return (
            <li key={`show-profile-${key}`}>
                <a href="javascript:;" onClick={(e) => handleBooleanFilterClear(e, key)}>
                    {label} <i className="icon-cross"></i>
                </a>
            </li>
        );
    });

    return (
        <>
            <FactLoader show={loading} />
            <section className="alumni_directory_filter container-fluid">
                <div className="max-1140 mx-auto">
                    <div className="JobInternshipListing">
                        <div className="ji_filtersec aos-init aos-animate">

                            {/* Mobile filter toggle */}
                            <div className="mobileFilter d-flex justify-content-between align-items-center d-md-none mobileonly text-end">
                                <button
                                    className="btn-filter btn-unscroll font-18 font-500 w-100"
                                    type="button"
                                    onClick={handleFilterToggle}>
                                    <i className="icon-filter"></i>
                                    <span>Filters</span>
                                </button>
                            </div>

                            <div className={`accordion collapse show directory_wrap ${isOpen ? 'open' : ''}`} id="filter_collapse">
                                <div className="filters-head">
                                    <div className="filters-head-left">
                                        <h3
                                            className="font-18 font-500 mb-0"
                                            role="button"
                                            onClick={() => setShowFilters(!showFilters)}
                                            data-bs-toggle="collapse"
                                            data-bs-target="#accordionFilters"
                                            aria-controls="accordionFilters"
                                            aria-expanded={showFilters ? 'true' : 'false'}>
                                            <span className="icon-filter"></span>
                                            <span>Filters</span>
                                        </h3>
                                        <div
                                            className={`badge rounded-pill text-bg-secondary py-2 px-3 bg-opacity-25 text-dark mt-3 mt-md-0  ${(state as any)?.flags?.isDataLoading ? ' is-loading' : ''}`}
                                            id="UsersCountsTop"
                                            aria-live="polite">
                                            <div className="directory-result-count__body">
                                                {(state as any)?.flags?.isDataLoading ? (
                                                    <span className="directory-result-count__label">Searching…</span>
                                                ) : (
                                                    <span className="directory-result-count__row">
                                                        <span className="directory-result-count__num font-14 font-500">
                                                            {((state as any)?.data?.students?.length ?? 0).toLocaleString()}
                                                        </span>
                                                        <span className="directory-result-count__label font-14 font-400">
                                                            {' '}
                                                            alumni found
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <a
                                        className="btn-clear btn-tab-clear d-block d-md-none"
                                        role="button"
                                        onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}>
                                        Clear
                                    </a>
                                    <div className="filters-head-right">
                                        <i
                                            className="btn-md-filter icon-right-arrow d-none d-md-block filters-head-toggle"
                                            role="button"
                                            onClick={() => setShowFilters(!showFilters)}
                                            data-bs-toggle="collapse"
                                            data-bs-target="#accordionFilters"
                                            aria-controls="accordionFilters"
                                            aria-expanded={showFilters ? 'true' : 'false'}></i>
                                    </div>
                                </div>

                                {showFilters && (
                                    <div className="accordion-collapse collapse show" id="accordionFilters" data-bs-parent="filter_collapse">
                                        <div className="filterGroup mt-3">
                                            {/* ── Main Filters ─────────────────────────────────── */}
                                            {renderFilterItems(
                                                filters.filter((c: any) => !c.isAdditional && c.isVisible && !c.isShowProfileFilter)
                                            )}

                                            <DirectoryShowProfileSection
                                                title="Show Experts"
                                                filterConfig={DomainExpertDirectoryService.filterConfig}
                                                filterValues={(state as any).data?.filters}
                                                onBooleanChange={handleBooleanFilterChange}
                                                listId="ShowExpertsList"
                                                tooltip="Filter domain experts by organization type and education background."
                                                part="desktop"
                                            />

                                            {/* ── Additional Filters ─────────────────────────── */}
                                            <div className="additionalfilter_wrap last mb-20">
                                                <a
                                                    href="javascript:;"
                                                    className={`btn-additionalfilter my-3 ${showAdditionalFilters ? '' : 'collapsed'}`}
                                                    onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
                                                    data-bs-toggle="collapse"
                                                    data-bs-target="#additionalfilter"
                                                    aria-expanded={showAdditionalFilters ? 'true' : 'false'}>
                                                    <span>ADDITIONAL FILTER</span>
                                                    <i className="btn-md-filter icon-right-arrow d-none d-md-block"></i>
                                                </a>
                                                {showAdditionalFilters && (
                                                    <div className="collapse show" id="additionalfilter">
                                                        <div className="filterGroup">
                                                            {renderFilterItems(
                                                                filters.filter(
                                                                    (c: any) =>
                                                                        c.isAdditional &&
                                                                        c.isVisible &&
                                                                        !c.isShowProfileFilter
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Mobile close */}
                                <div className="d-flex d-md-none filter_close_apply">
                                    <a
                                        className="btn-filter-close collapsed"
                                        onClick={handleFilterToggle}
                                        href="javascript:;">
                                        Close
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Applied filter chips */}
            <section>
                <div className="max-1140 mx-auto mb-3">
                    <div className="row">
                        <div className="filterresult border-0 mb-0">
                            <ul>
                                {appliedFilterChips}
                                {showProfileFilterChips}
                                {hasAppliedFilters && (
                                    <li className="clear">
                                        <a
                                            className="btn-clear"
                                            role="button"
                                            onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}>
                                            Clear All
                                        </a>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default DomainExpertSearchControl;